import classNames from 'classnames';
import ouibounce from 'ouibounce';
import React, {useCallback, useContext, useEffect, useMemo, useRef} from 'react';

import {
    GwgAccelerator,
    GwgCarousel,
    InDemandCareer,
    InDemandCareerWrapper,
    AcademyPresented,
    AcademySelected,
} from '@udemy/career-academies';
import {AboveTheFoldProvider} from '@udemy/design-system-utils';
import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {TestimonialsCMSWrapper} from '@udemy/react-cms-components';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {
    EnhancedCategoriesComponent,
    EntryPointPopUp,
    TopCategoriesComponent,
} from '@udemy/react-globalization-components';
import {
    OnePaneLayoutContainer,
    OnePaneLayoutFullWidthSection,
} from '@udemy/react-layout-components';
import {
    CMSConnectedPartnersBanner,
    DynamicWebContentPageType,
} from '@udemy/shared-one-udemy-components';
import {useActiveSubscriptions} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {AIEraCareerBanner} from 'src/components/ai-career-banner/ai-career-banner.react-component';
import {DiscoveryUnits} from 'src/components/discovery-units/discovery-units.react-component';
import {EnrichedSkillsContainer} from 'src/components/enriched-skills/enriched-skills-container.react-component';
import {MarketingHeroBanner} from 'src/components/hero-banner/index';
import {
    ProfessionalSkillsUnit,
    PSUSkeleton,
} from 'src/components/professional-skills-showcase/professional-skills-showcase.react-component';
import {TrendingSkills} from 'src/components/trending-skills/trending-skills.react-component';
import {ValuePropModule} from 'src/components/value-prop-module/value-prop-module.react-component';
import {
    CAREER_ACADEMIES,
    LOHP_VERTICAL_MOBILE_RECO_UNITS,
    POPULAR_CATEGORIES_MODULE,
    POPULAR_CATEGORIES_MODULE_FOR_LOHP,
    SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER,
    SHOW_VERNACULAR_CONTENT,
    MWEB_SEARCH_HEADER_OPTIMIZATION,
} from 'src/lib/experimentation-helper';

import {CertPrepModule} from '../../components/cert-prep-module/cert-prep-module.react-component';
import {ProfessionalSkillsShowcaseDataContext} from './contexts/professional-skills-showcase-data-context';
import {useFetchHeroBanners} from './hooks/useFetchHeroBanners';
import {useIsGwgAvailable} from './hooks/useIsGwgAvailable';
import {useVerifyIsUsMarketplace} from './hooks/useVerifyIsUsMarketplace';
import styles from './logged-out-homepage-body.module.less';

export interface LoggedOutHomePageBodyProps {
    academies: InDemandCareer[];
    accelerator: GwgAccelerator | null;
}

export const LoggedOutHomePageBody = ({academies, accelerator}: LoggedOutHomePageBodyProps) => {
    const isMobileMax = useMatchMedia('mobile-max');
    const {locale, gettext} = useI18n();
    const isLanguageLocaleJapanese = locale === 'ja_JP';
    const {isUSMarketplace} = useVerifyIsUsMarketplace(locale);
    const psuData = useContext(ProfessionalSkillsShowcaseDataContext);
    const {me, Config: udConfig} = useUDData();
    const hasTrackedExitIntent = useRef(false);
    const isPriceCountryJapan = udConfig.price_country?.id === 'JP';
    // PSU re-ordering Experiment - https://udemyjira.atlassian.net/browse/TB-10066
    const {isLoading, featureVariantAssignments: features} = useFeatures([
        CAREER_ACADEMIES,
        POPULAR_CATEGORIES_MODULE,
        POPULAR_CATEGORIES_MODULE_FOR_LOHP,
        SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER,
        LOHP_VERTICAL_MOBILE_RECO_UNITS,
        SHOW_VERNACULAR_CONTENT,
        MWEB_SEARCH_HEADER_OPTIMIZATION,
    ]);
    // The hook fires the variant (lohp_hero_banner) and control
    // (banner_carousel_slide) requests in parallel, preferring the variant
    // when it succeeds. The resulting noticeType drives which hero banner the
    // UI renders (see WithConsolidatedHeroBanner).
    const {heroBanners, isHeroBannerLoading, noticeType} = useFetchHeroBanners();
    const showPopularCategoriesModule =
        features.get(POPULAR_CATEGORIES_MODULE)?.configuration ?? {};
    const showTrendingSkillsAboveAIEraCareerBanner =
        features.get(SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER)?.configuration ?? {};
    const showTrendingSkillsAboveForIndia =
        showTrendingSkillsAboveAIEraCareerBanner.enabled_for_in ?? false;
    const showTrendingSkillsAboveForJapan =
        showTrendingSkillsAboveAIEraCareerBanner.enabled_for_japan ?? false;

    const showVernacularContentConfig = features.get(SHOW_VERNACULAR_CONTENT);
    const showVernacularContent =
        showVernacularContentConfig?.configuration?.showVernacularContent ?? false;

    const showOptimizedSearchHeaderMWeb =
        features.get(MWEB_SEARCH_HEADER_OPTIMIZATION)?.configuration
            ?.showOptimizedSearchHeaderMWeb ?? false;
    const showBannerSearchBar = !showOptimizedSearchHeaderMWeb;

    const currentlySubscribedPlan = useActiveSubscriptions()?.[0];
    // Memoize subscription plans object to prevent unnecessary carousel re-renders
    const subscriptionPlans = useMemo(
        () => ({
            currentlySubscribedPlan,
        }),
        [currentlySubscribedPlan],
    );
    const isGwgAvailable = useIsGwgAvailable();

    // Hide Learners Also Viewing Section on certain locales with low course supply
    const shouldHidePSUOnLowCourseSupplyLocale = ['nl_NL', 'ro_RO', 'th_TH', 'vi_VN'].includes(
        locale,
    );

    const isCareerAcademiesEnabled = features.get(CAREER_ACADEMIES)?.configuration?.feature;
    const displayedAcademies = academies.length > 3 ? academies.slice(0, 3) : academies;

    const isVerticalRecoUnitsFeatureEnabled = features.get(LOHP_VERTICAL_MOBILE_RECO_UNITS)
        ?.configuration?.useVerticalLayout;

    const showAddToCartButtonsInVerticalLayout = features.get(LOHP_VERTICAL_MOBILE_RECO_UNITS)
        ?.configuration?.showAddToCartButtons;

    const onEnrollmentClick = () => {
        if (accelerator) {
            Tracker.publishEvent(
                new AcademySelected({
                    academySlug: accelerator.slug,
                    uiRegion: 'learn-ai-with-google',
                }),
            );
        }
    };

    const trackAcademyPresented = useCallback(() => {
        if (accelerator) {
            Tracker.publishEvent(
                new AcademyPresented({
                    academySlug: accelerator.slug,
                    uiRegion: 'learn-ai-with-google',
                }),
            );
        }
    }, [accelerator]);

    useEffect(() => {
        const dummyModal = document.createElement('div');

        ouibounce(dummyModal, {
            aggressive: true,
            timer: 0,
            callback: () => {
                if (me.is_authenticated || hasTrackedExitIntent.current) return;
                window.appboy?.logCustomEvent('exit_intent', {page: 'LoggedOutHomePage'});
                hasTrackedExitIntent.current = true;
            },
        });
    }, [me.is_authenticated]);

    return (
        <div data-testid="lohp-body">
            <OnePaneLayoutContainer>
                <AboveTheFoldProvider>
                    <section>
                        <MarketingHeroBanner
                            heroBanners={heroBanners}
                            noticeType={noticeType}
                            isLoading={isHeroBannerLoading}
                            showBannerSearchBar={showBannerSearchBar}
                        />
                    </section>
                    <EnrichedSkillsContainer />

                    {isPriceCountryJapan &&
                        showPopularCategoriesModule.show_module_at_top &&
                        (showPopularCategoriesModule.show_enhanced_categories_module ||
                            showPopularCategoriesModule.show_enhanced_categories_module_for_mobile) && (
                            <section className={styles['top-categories']}>
                                <EnhancedCategoriesComponent />
                            </section>
                        )}

                    <OnePaneLayoutFullWidthSection>
                        <section
                            id="discovery-units-top"
                            className={styles['ud-container-with-common-padding']}
                        >
                            <DiscoveryUnits
                                isVerticalRecoUnitsFeatureEnabled={
                                    isVerticalRecoUnitsFeatureEnabled
                                }
                                showAddToCartButtonsInVerticalLayout={
                                    showAddToCartButtonsInVerticalLayout
                                }
                            />
                        </section>
                    </OnePaneLayoutFullWidthSection>

                    {isLanguageLocaleJapanese && <ValuePropModule />}

                    {showTrendingSkillsAboveForIndia && (
                        <>
                            <div className={styles['section-top-spacer']} />
                            <section>
                                <OnePaneLayoutFullWidthSection
                                    className={styles['fill-background']}
                                >
                                    <div className={styles['ud-container-with-common-padding']}>
                                        <TrendingSkills />
                                    </div>
                                </OnePaneLayoutFullWidthSection>
                            </section>
                        </>
                    )}
                    <section>
                        <AIEraCareerBanner />
                    </section>

                    {!shouldHidePSUOnLowCourseSupplyLocale && (
                        <section className={styles['professionals-skills-module']}>
                            <ProfessionalSkillsUnit
                                pageType="logged_out_homepage"
                                contentPanelClassName={styles['psu-subcontent-panel']}
                                unitData={psuData}
                                loadingSkeleton={isLoading ? <PSUSkeleton /> : null}
                                isVerticalRecoUnitsFeatureEnabled={
                                    isVerticalRecoUnitsFeatureEnabled
                                }
                                showAddToCartButtonsInVerticalLayout={
                                    showAddToCartButtonsInVerticalLayout
                                }
                            />
                        </section>
                    )}
                    {showVernacularContent && (
                        <OnePaneLayoutFullWidthSection>
                            <section
                                id="discovery-units-top"
                                className={styles['vernacular-content']}
                            >
                                <DiscoveryUnits
                                    isVerticalRecoUnitsFeatureEnabled={
                                        isVerticalRecoUnitsFeatureEnabled
                                    }
                                    showAddToCartButtonsInVerticalLayout={
                                        showAddToCartButtonsInVerticalLayout
                                    }
                                    showVernacularContent={showVernacularContent}
                                    uiRegion="vernacular_lohp"
                                />
                            </section>
                        </OnePaneLayoutFullWidthSection>
                    )}
                    <section>
                        <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                            <div className={styles['partners-banner-wrapper']}>
                                <CMSConnectedPartnersBanner
                                    pageType={DynamicWebContentPageType.LoggedOutHomePage}
                                />
                            </div>
                        </OnePaneLayoutFullWidthSection>
                    </section>

                    <section>
                        <OnePaneLayoutFullWidthSection>
                            <div className={styles['ud-container-with-common-padding']}>
                                <TestimonialsCMSWrapper
                                    showCta={!isMobileMax}
                                    ctaHref="https://about.udemy.com/stories/"
                                    ctaLabel={gettext('View all stories')}
                                    titleClassName="ud-heading-xl"
                                />
                            </div>
                        </OnePaneLayoutFullWidthSection>
                    </section>

                    <CertPrepModule />

                    {isCareerAcademiesEnabled && !!displayedAcademies?.length && (
                        <section className={styles['academies-list']}>
                            <h2
                                className={classNames(
                                    'ud-heading-xxl',
                                    styles['academies-list-heading'],
                                )}
                            >
                                {gettext('Ready to reimagine your career?')}
                            </h2>
                            <div className={styles['academies-list-subtitle']}>
                                {gettext(
                                    'Get the skills and real-world experience employers want with Career Accelerators.',
                                )}
                            </div>
                            <InDemandCareerWrapper
                                academies={displayedAcademies}
                                className={styles['academies-list-wrapper']}
                                headingProps={{className: styles['hide']}}
                                isUSMarketplace={isUSMarketplace}
                                buttonProps={{udStyle: 'ghost'}}
                            />
                        </section>
                    )}
                </AboveTheFoldProvider>
                <EntryPointPopUp />
                {showPopularCategoriesModule.show_module_at_bottom && (
                    <>
                        {(showPopularCategoriesModule.show_enhanced_categories_module ||
                            showPopularCategoriesModule.show_enhanced_categories_module_for_mobile) && (
                            <section className={styles['top-categories']}>
                                <EnhancedCategoriesComponent />
                            </section>
                        )}
                        {isPriceCountryJapan &&
                            !showPopularCategoriesModule.show_enhanced_categories_module &&
                            !showPopularCategoriesModule.show_enhanced_categories_module_for_mobile && (
                                <section className={styles['top-categories']}>
                                    <TopCategoriesComponent />
                                </section>
                            )}
                    </>
                )}
                {!showTrendingSkillsAboveForIndia && (
                    <>
                        <div className={styles['section-top-spacer']} />
                        <section>
                            <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                                <div className={styles['ud-container-with-common-padding']}>
                                    <TrendingSkills
                                        isJapanTopSkillExperimentActive={
                                            showTrendingSkillsAboveForJapan
                                        }
                                    />
                                </div>
                            </OnePaneLayoutFullWidthSection>
                        </section>
                    </>
                )}
                {showTrendingSkillsAboveForIndia && (
                    <div className={styles['section-top-spacer']} />
                )}
                <section>
                    <OnePaneLayoutFullWidthSection>
                        <div className={styles['ud-container-with-common-padding']}>
                            {isGwgAvailable && accelerator && (
                                <TrackImpression trackFunc={trackAcademyPresented}>
                                    <div className={styles['grow-with-google-banner']}>
                                        <h2 className="ud-heading-xxl">
                                            {gettext('Learn AI with Google')}
                                        </h2>
                                        <GwgCarousel
                                            accelerator={accelerator}
                                            currentlySubscribedPlan={
                                                subscriptionPlans.currentlySubscribedPlan
                                            }
                                            onEnrollmentClick={onEnrollmentClick}
                                        />
                                    </div>
                                </TrackImpression>
                            )}
                        </div>
                    </OnePaneLayoutFullWidthSection>
                </section>
            </OnePaneLayoutContainer>
        </div>
    );
};
